/**
 * HeaderScroll Module - Pure Tailwind approach
 * NO inline styles - only class manipulation
 */

class HeaderScroll {
	constructor() {
		// Configuration using arrays
		this.CONFIG = {
			SCROLL_THRESHOLD: 100,
			SELECTORS: {
				header: '.ui-h',
				meta: '.ui-h-meta',
				main: '.ui-h-main',
				logo: '.ui-h-logo',
				container: '.ui-h-c',
				mainContent: '.ui-mc',
			},
			STATES: {
				header: {
					normal: ['shadow-sm'],
					scrolled: ['shadow-lg'],
				},
				meta: {
					visible: ['max-h-10', 'opacity-100', 'py-1'],
					hidden: ['max-h-10', 'opacity-100', 'py-1'],
				},
				logo: {
					normal: ['scale-60', 'lg:scale-100', 'origin-left'],
					scrolled: ['scale-60', 'lg:scale-100', 'origin-left'],
				},
				container: {
					normal: ['h-[109px]', 'lg:h-[73px]'],
					scrolled: ['h-[109px]', 'lg:h-[73px]'],
				},
				mainContent: {
					normal: ['mt-[109px]', 'lg:mt-[109px]'],
					scrolled: ['mt-[109px]', 'lg:mt-[109px]'],
				},
			},
		}

		// Get elements
		this.elements = this.getElements()

		// Initialize if header exists
		if (this.elements.header && this.elements.main) {
			this.init()
		}
	}

	getElements() {
		const { SELECTORS } = this.CONFIG
		return {
			header: document.querySelector(SELECTORS.header),
			meta: document.querySelector(SELECTORS.meta),
			main: document.querySelector(SELECTORS.main),
			logo: document.querySelector(SELECTORS.logo),
			container: document.querySelector(SELECTORS.container),
			mainContent: document.querySelector(SELECTORS.mainContent),
		}
	}

	init() {
		// Throttled scroll handler
		let ticking = false
		window.addEventListener('scroll', () => {
			if (!ticking) {
				requestAnimationFrame(() => {
					this.handleScroll()
					ticking = false
				})
				ticking = true
			}
		})

		// Check initial state
		this.handleScroll()

		// Enable transitions only after the initial state has been applied.
		requestAnimationFrame(() => {
			this.elements.header?.setAttribute('data-scroll-ready', 'true')
		})
	}

	handleScroll() {
		const scrolled = window.pageYOffset > this.CONFIG.SCROLL_THRESHOLD

		if (scrolled) {
			this.shrink()
		} else {
			this.expand()
		}
	}

	shrink() {
		// Check if already scrolled using attribute (not style check!)
		if (this.elements.header?.hasAttribute('scrolled')) return

		const { STATES } = this.CONFIG
		const { header, meta, logo, container, mainContent } = this.elements

		// Set state attribute
		header?.setAttribute('scrolled', 'true')

		// Swap classes using arrays - NO inline styles!
		this.swapClasses(header, STATES.header.normal, STATES.header.scrolled)
		this.swapClasses(meta, STATES.meta.visible, STATES.meta.hidden)
		this.swapClasses(logo, STATES.logo.normal, STATES.logo.scrolled)
		this.swapClasses(container, STATES.container.normal, STATES.container.scrolled)
		this.swapClasses(mainContent, STATES.mainContent.normal, STATES.mainContent.scrolled)
	}

	expand() {
		// Check if already expanded
		if (!this.elements.header?.hasAttribute('scrolled')) return

		const { STATES } = this.CONFIG
		const { header, meta, logo, container, mainContent } = this.elements

		// Remove state attribute
		header?.removeAttribute('scrolled')

		// Swap classes back - NO inline styles!
		this.swapClasses(header, STATES.header.scrolled, STATES.header.normal)
		this.swapClasses(meta, STATES.meta.hidden, STATES.meta.visible)
		this.swapClasses(logo, STATES.logo.scrolled, STATES.logo.normal)
		this.swapClasses(container, STATES.container.scrolled, STATES.container.normal)
		this.swapClasses(mainContent, STATES.mainContent.scrolled, STATES.mainContent.normal)
	}

	/**
	 * Helper to swap class arrays cleanly
	 */
	swapClasses(element, remove, add) {
		if (!element) return
		element.classList.remove(...remove)
		element.classList.add(...add)
	}
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => new HeaderScroll())
} else {
	new HeaderScroll()
}

// Export for use
window.HeaderScroll = HeaderScroll
